// Importa o cliente Supabase
import { supabase } from '../config/supabase';

// A interface PatientLinkInfo pode ser removida ou adaptada conforme a necessidade real de uso.
// No contexto atual, as informações de link estão embutidas nos próprios registros de usuário e paciente.
// Se ainda for necessário um objeto para representar um link, ele pode ser:
export interface PatientLinkInfo {
  user_id: string; // Corresponde ao `id` do usuário
  patient_record_id: string; // Corresponde ao `id` do paciente
  linked_at: string; // ISO string de data
  linked_by: string; // userId de quem realizou o link
}

export interface UserWithLinkStatus {
  uid: string; // Corresponde ao `id` na tabela `users` do Supabase
  email: string;
  displayName: string; // Pode ser mapeado de `display_name` no Supabase
  role: string;
  isLinked: boolean;
  linkedPatientId?: string; // Corresponde ao `patient_record_id` na tabela `users` do Supabase
  linkedPatientName?: string; // Virá do JOIN com a tabela `patients`
}

export interface PatientWithLinkStatus {
  id: string; // Corresponde ao `id` na tabela `patients` do Supabase
  prontuario: string;
  nome: string;
  sobrenome: string;
  email?: string;
  cpf?: string;
  isLinked: boolean;
  linkeduserId?: string; // Corresponde ao `user_id` na tabela `patients` do Supabase
  linkedUserEmail?: string; // Virá do JOIN com a tabela `users`
}

export class PatientLinkingService {
  private static readonly USERS_TABLE = 'users'; // Renomeado para seguir convenção do Supabase
  private static readonly PATIENTS_TABLE = 'patients'; // Renomeado para seguir convenção do Supabase

  static async linkPatientToUser(
    userId: string,
    patientRecordId: string,
    _linkedByuserId: string,
    clientId: string
  ): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para vinculação.');
      }

      // 1. Busca o usuário para garantir que ele existe e tem a role 'patient'
      const { data: userData, error: userError } = await supabase
        .from(this.USERS_TABLE)
        .select(`
          userId,
          user_clients!inner(userRole, clientId)
        `)
        .eq('userId', userId)
        .eq('user_clients.clientId', clientId)
        .maybeSingle();

      if (userError) throw new Error(`Erro ao buscar usuário: ${userError.message}`);
      if (!userData) throw new Error('Usuário não encontrado neste tenant');
      
      const userRole = userData.user_clients?.[0]?.userRole;
      if (userRole !== 'patient') {
        throw new Error('Apenas usuários com role "patient" podem ser vinculados.');
      }

      // 2. Busca o paciente para garantir que ele existe
      const { data: patientData, error: patientError } = await supabase
        .from(this.PATIENTS_TABLE)
        .select(`
          patientId,
          userId,
          patient_clients!inner(clientId)
        `)
        .eq('patientId', patientRecordId)
        .eq('patient_clients.clientId', clientId)
        .maybeSingle();

      if (patientError) throw new Error(`Erro ao buscar paciente: ${patientError.message}`);
      if (!patientData) throw new Error('Cadastro de paciente não encontrado neste tenant');

      // 3. Verifica se o paciente ou o usuário já estão vinculados a outra pessoa
      if (patientData.userId && patientData.userId !== userId) {
        throw new Error('Este cadastro de paciente já está vinculado a outro usuário.');
      }

      const { data: existingPatientLink, error: existingLinkError } = await supabase
        .from(this.PATIENTS_TABLE)
        .select(`
          patientId,
          patient_clients!inner(clientId)
        `)
        .eq('userId', userId)
        .eq('patient_clients.clientId', clientId)
        .maybeSingle();

      if (existingLinkError && existingLinkError.code !== 'PGRST116') { // Ignora erro 'not found'
          throw new Error(`Erro ao verificar vínculo existente: ${existingLinkError.message}`);
      }

      if (existingPatientLink && existingPatientLink.patientId !== patientRecordId) {
        throw new Error('Este usuário já está vinculado a outro cadastro de paciente.');
      }

      // 4. Atualiza a tabela 'patients' com o 'userId'
      const { error: updatePatientError } = await supabase
        .from(this.PATIENTS_TABLE)
        .update({ 
          userId: userId,
          patientUpdatedAt: new Date().toISOString()
         })
        .eq('patientId', patientRecordId);

      if (updatePatientError) {
        throw new Error(`Erro ao atualizar paciente: ${updatePatientError.message}`);
      }

      console.log(`✅ Vinculação criada: userId=${userId} <-> patientRecordId=${patientRecordId}`);
    } catch (error) {
      console.error('❌ Erro ao vincular paciente ao usuário:', error);
      throw error;
    }
  }

  static async unlinkPatientFromUser(userId: string, patientRecordId: string, clientId: string): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para desvinculação.');
      }

      const { data: scopedPatient, error: scopedPatientError } = await supabase
        .from(this.PATIENTS_TABLE)
        .select(`
          patientId,
          patient_clients!inner(clientId)
        `)
        .eq('patientId', patientRecordId)
        .eq('patient_clients.clientId', clientId)
        .maybeSingle();

      if (scopedPatientError) {
        throw new Error(`Erro ao validar paciente no tenant: ${scopedPatientError.message}`);
      }

      if (!scopedPatient) {
        throw new Error('Paciente não pertence ao tenant selecionado.');
      }

      // A única operação necessária é remover o 'userId' do registro do paciente.
      const { error: patientUpdateError } = await supabase
        .from(this.PATIENTS_TABLE)
        .update({ 
          userId: null,
          patientUpdatedAt: new Date().toISOString()
        })
        .eq('patientId', patientRecordId)
        .eq('userId', userId); // Garante que estamos desvinculando o par correto

      if (patientUpdateError) {
        throw new Error(`Erro ao desvincular paciente: ${patientUpdateError.message}`);
      }

      console.log(`✅ Vinculação removida: userId=${userId} <-> patientRecordId=${patientRecordId}`);
    } catch (error) {
      console.error("❌ Erro ao desvincular paciente do usuário:", error);
      throw error;
    }
  }

  static async getPatientRecordByuserId(userId: string, clientId?: string): Promise<any | null> {
    try {
      if (!clientId) {
        return null;
      }

      // Busca paciente vinculado ao usuário e, quando informado, restringe ao tenant atual.
      const query = supabase
        .from(this.PATIENTS_TABLE)
        .select(`
          *,
          patient_clients!inner(clientId)
        `)
        .eq('userId', userId)
        .eq('patient_clients.clientId', clientId);

      const { data: patientData, error: patientError } = await query.maybeSingle();

      if (patientError) {
        console.error(`Erro ao buscar paciente por userId: ${patientError.message}`);
        return null;
      }

      return patientData; // Retorna o objeto completo do paciente, ou null se não encontrado
    } catch (error) {
      console.error("❌ Erro ao buscar cadastro de paciente pelo userId:", error);
      throw error;
    }
  }

  static async getUserByPatientRecordId(patientRecordId: string, clientId?: string): Promise<any | null> {
    try {
      if (!clientId) {
        return null;
      }

      const { data: patientData, error: patientError } = await supabase
        .from(this.PATIENTS_TABLE)
        .select(`
          userId,
          patient_clients!inner(clientId)
        `)
        .eq('patientId', patientRecordId)
        .eq('patient_clients.clientId', clientId)
        .maybeSingle();

      if (patientError) {
        console.error(`Erro ao buscar paciente: ${patientError.message}`);
        return null;
      }

      if (!patientData || !patientData.userId) {
        return null;
      }

      const { data: userData, error: userError } = await supabase
        .from(this.USERS_TABLE)
        .select('*') // Seleciona todas as colunas do usuário
        .eq('userId', patientData.userId)
        .maybeSingle();

      if (userError) {
        console.error(`Erro ao buscar usuário: ${userError.message}`);
        return null;
      }

      if (!userData) {
        return null;
      }

      // O Supabase retorna os dados diretamente
      return userData;
    } catch (error) {
      console.error("❌ Erro ao buscar usuário pelo patientRecordId:", error);
      throw error;
    }
  }

  static async getAllUsersWithLinkStatus(clientId: string): Promise<UserWithLinkStatus[]> {
    try {
      if (!clientId) {
        return [];
      }

      const { data: usersData, error: userError } = await supabase
        .from('users')
        .select(`
          userId,
          userEmail,
          userDisplayName,
          user_clients!inner(userRole, clientId)
        `)
        .eq('user_clients.userRole', 'patient')
        .eq('user_clients.clientId', clientId);

      if (userError) {
        throw new Error(`Erro ao buscar usuários: ${userError.message}`);
      }

      if (!usersData) {
        return [];
      }

      const userIds = usersData
        .map((userData: any) => userData.userId)
        .filter(Boolean);

      let linkedPatientsByUserId = new Map<string, { patientId: string; patientFullName: string }>();

      if (userIds.length > 0) {
        const { data: linkedPatients, error: linkedPatientsError } = await supabase
          .from('patients')
          .select(`
            patientId,
            patientFullName,
            userId,
            patient_clients!inner(clientId)
          `)
          .in('userId', userIds)
          .eq('patient_clients.clientId', clientId);

        if (linkedPatientsError) {
          throw new Error(`Erro ao buscar pacientes vinculados: ${linkedPatientsError.message}`);
        }

        linkedPatientsByUserId = new Map(
          (linkedPatients || [])
            .filter((patient: any) => !!patient?.userId)
            .map((patient: any) => [
              patient.userId,
              {
                patientId: patient.patientId,
                patientFullName: patient.patientFullName,
              },
            ])
        );
      }

      const users: UserWithLinkStatus[] = usersData.map((userData: any) => {
        const linkedPatient = linkedPatientsByUserId.get(userData.userId) || null;
        const isLinked = !!linkedPatient;
        const userRole = userData.user_clients && userData.user_clients.length > 0 ? userData.user_clients[0].userRole : 'patient';

        return {
          uid: userData.userId,
          email: userData.userEmail || '',
          displayName: userData.userDisplayName || '',
          role: userRole,
          isLinked,
          linkedPatientId: isLinked ? linkedPatient.patientId : undefined,
          linkedPatientName: isLinked ? linkedPatient.patientFullName : undefined,
        };
      });

      return users;
    } catch (error) {
      console.error('❌ Erro ao buscar usuários com status de vinculação:', error);
      throw error;
    }
  }

  static async getAllPatientsWithLinkStatus(clientId: string): Promise<PatientWithLinkStatus[]> {
    try {
      if (!clientId) {
        return [];
      }

      const { data: patientClientsData, error: patientError } = await supabase
        .from("patient_clients")
        .select(
          `
          patientId,
          patients!inner(
            patientId,
            patientFullName,
            patientCpf,
            userId,
            users!patients_userId_fkey ( userEmail )
          )
          `
        )
        .eq('clientId', clientId);

      if (patientError) {
        throw new Error(`Erro ao buscar pacientes: ${patientError.message}`);
      }

      const patients: PatientWithLinkStatus[] = (patientClientsData || []).map((patientClient: any) => {
        const patient = Array.isArray(patientClient.patients) ? patientClient.patients[0] : patientClient.patients;
        if (!patient) {
          return null;
        }
        const linkedUser = Array.isArray(patient?.users) ? patient.users[0] : patient?.users;
        const isLinked = !!patient.userId;
        
        return {
          id: patient.patientId,
          prontuario: patient.patientId, // Using patientId as prontuario
          nome: patient.patientFullName,
          sobrenome: "", // No separate sobrenome field
          email: linkedUser?.userEmail,
          cpf: patient.patientCpf,
          isLinked,
          linkeduserId: patient.userId,
          linkedUserEmail: linkedUser?.userEmail,
        };
      }).filter((patient): patient is PatientWithLinkStatus => patient !== null);

      return patients;
    } catch (error) {
      console.error("❌ Erro ao buscar pacientes com status de vinculação:", error);
      throw error;
    }
  }

  static async findPotentialMatches(userId: string, clientId: string): Promise<PatientWithLinkStatus[]> {
    try {
      if (!clientId) {
        return [];
      }

      const { data: userData, error: userError } = await supabase
        .from(this.USERS_TABLE)
        .select(
          `
          userId,
          userEmail,
          userDisplayName,
          user_clients!inner(clientId)
          `
        )
        .eq('userId', userId)
        .eq('user_clients.clientId', clientId)
        .maybeSingle();

      if (userError) {
        throw new Error(`Erro ao buscar usuário: ${userError.message}`);
      }

      if (!userData) {
        throw new Error('Usuário não encontrado');
      }

      const userEmail = userData.userEmail?.toLowerCase();

      if (!userEmail) {
        return [];
      }

      const allPatients = await this.getAllPatientsWithLinkStatus(clientId);

      const matches = allPatients.filter(patient => {
        if (patient.isLinked) return false;

        const patientEmail = patient.email?.toLowerCase();
        if (patientEmail && patientEmail === userEmail) {
          return true;
        }

        const userName = userData.userDisplayName?.toLowerCase() || '';
        const patientFullName = `${patient.nome} ${patient.sobrenome}`.toLowerCase();

        if (userName && patientFullName.includes(userName)) {
          return true;
        }

        return false;
      });

      return matches;
    } catch (error) {
      console.error('❌ Erro ao buscar correspondências potenciais:', error);
      throw error;
    }
  }

  static async getLinkingStatistics(clientId: string): Promise<{
    totalUsers: number;
    totalPatients: number;
    linkedUsers: number;
    unlinkedUsers: number;
    linkedPatients: number;
    unlinkedPatients: number;
  }> {
    try {
      // Reutiliza os métodos já migrados para Supabase
      const [users, patients] = await Promise.all([
        this.getAllUsersWithLinkStatus(clientId),
        this.getAllPatientsWithLinkStatus(clientId)
      ]);

      return {
        totalUsers: users.length,
        totalPatients: patients.length,
        linkedUsers: users.filter(u => u.isLinked).length,
        unlinkedUsers: users.filter(u => !u.isLinked).length,
        linkedPatients: patients.filter(p => p.isLinked).length,
        unlinkedPatients: patients.filter(p => !p.isLinked).length
      };
    } catch (error) {
      console.error("❌ Erro ao calcular estatísticas:", error);
      throw error;
    }
  }
}
